import React from "react";
import { cn } from "@shadcn-in-nx/utils";
import {
    FileText, Search, Code, Trash2, Edit3, PlusCircle, FolderSearch,
    Globe, HelpCircle, ListTodo, FileCode, RefreshCw, MessageSquare,
} from "lucide-react";

const ArgRow = ({ label, value, icon: Icon }) => {
    if (!value && value !== false && value !== 0) return null;
    return (
        <div className="flex items-start gap-2 py-1 border-b border-gray-100 last:border-0">
            {Icon && <Icon className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />}
            <span className="text-[10px] font-medium text-gray-500 w-20 flex-shrink-0">{label}</span>
            <span className="text-xs text-gray-800 break-all">{String(value)}</span>
        </div>
    );
};

const FilesList = ({ files }) => (
    <div className="flex flex-wrap gap-1 mt-1">
        {files?.map((f, i) => (
            <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                {typeof f === "string" ? f.split("/").pop() : f}
            </span>
        ))}
    </div>
);

const CodePreview = ({ code, maxLines = 8}) => {
    const [viewMore, setViewMore ] = React.useState(false);
    if (!code) return null;
    const lines = code.split("\n");
    
    const preview = lines.slice(0, viewMore ? 99999 :  maxLines).join("\n");
    const hasMore = viewMore ? false : lines.length > maxLines;
    

    return (
        <div className="mt-1">
            <pre className="text-[10px] bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto ">
                {preview}
                {hasMore && <button className="text-gray-500 " onClick={() => setViewMore(true)}>\n... ({lines.length - maxLines} more lines)</button>}
            </pre>
        </div>
    );
};

const toolConfigs = {
    read_file: { icon: FileText, label: "Read File", color: "blue" },
    read_files: { icon: FileText, label: "Read Files", color: "blue" },
    execute_code: { icon: Code, label: "Execute Code", color: "purple" },
    create_file: { icon: PlusCircle, label: "Create File", color: "green" },
    update_file: { icon: Edit3, label: "Update File", color: "amber" },
    edit_file: { icon: Edit3, label: "Edit File", color: "amber" },
    delete_file: { icon: Trash2, label: "Delete File", color: "red" },
    rename_file: { icon: RefreshCw, label: "Rename File", color: "orange" },
    search_in_files: { icon: Search, label: "Search in Files", color: "cyan" },
    grep_search: { icon: Search, label: "Grep Search", color: "cyan" },
    glob_search: { icon: FolderSearch, label: "Glob Search", color: "indigo" },
    explore_codebase: { icon: FolderSearch, label: "Explore Codebase", color: "violet" },
    search_api: { icon: Globe, label: "Search API", color: "teal" },
    answer_platform_question: { icon: HelpCircle, label: "Platform Q&A", color: "pink" },
    report_user_feedback: { icon: MessageSquare, label: "User Feedback", color: "orange" },
    todo_write: { icon: ListTodo, label: "Todo Write", color: "slate" },
    start_code_generation: { icon: Code, label: "Start Generation", color: "emerald" },
    read_application_logs: { icon: FileCode, label: "Read Logs", color: "gray" },
};

export default function ToolArgumentsView({ toolName, args }) {
    const config = toolConfigs[toolName] || { icon: Code, label: toolName, color: "gray" };
    const Icon = config.icon;

    const renderToolSpecificArgs = () => {
        switch (toolName) {
            case "read_file":
                return (
                    <>
                        <ArgRow label="File" value={args.file_path} />
                        {args.offset && <ArgRow label="Offset" value={args.offset} />}
                        {args.limit && <ArgRow label="Limit" value={args.limit} />}
                        {args.reason && <ArgRow label="Reason" value={args.reason} />}
                    </>
                );
            case "read_files":
                return (
                    <>
                        <div className="text-xs text-gray-500 mb-1">Files to read:</div>
                        <FilesList files={args.files} />
                        {args.reason && <ArgRow label="Reason" value={args.reason} />}
                    </>
                );
            case "execute_code":
                return (
                    <>
                        <ArgRow label="Description" value={args.description} />
                        <CodePreview code={args.code} />
                    </>
                );
            case "create_file":
            case "update_file":
                return (
                    <>
                        <ArgRow label="File Path" value={args.filePath} icon={FileText} />
                        <ArgRow label="Change Type" value={args.changeType} />
                        <ArgRow label="Description" value={args.description} />
                        <CodePreview code={args.code} />
                    </>
                );
            case "edit_file":
                return (
                    <>
                        <ArgRow label="File Path" value={args.file_path} icon={FileText} />
                        <ArgRow label="Replace All" value={args.replace_all ? "Yes" : "No"} />
                        {args.old_string && (
                            <div className="mt-1">
                                <div className="text-[10px] text-gray-500 mb-0.5">Old:</div>
                                <pre className="text-[10px] bg-red-50 text-red-800 p-1.5 rounded max-h-20 overflow-auto">
                                    {args.old_string}
                                </pre>
                            </div>
                        )}
                        {args.new_string && (
                            <div className="mt-1">
                                <div className="text-[10px] text-gray-500 mb-0.5">New:</div>
                                <pre className="text-[10px] bg-green-50 text-green-800 p-1.5 rounded max-h-20 overflow-auto">
                                    {args.new_string}
                                </pre>
                            </div>
                        )}
                    </>
                );
            case "delete_file":
                return (
                    <>
                        <ArgRow label="File Path" value={args.filePath} icon={Trash2} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "rename_file":
                return (
                    <>
                        <ArgRow label="Old Path" value={args.oldFilePath} />
                        <ArgRow label="New Path" value={args.newFilePath} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "search_in_files":
                return (
                    <>
                        <ArgRow label="Search Term" value={args.searchTerm} icon={Search} />
                        <ArgRow label="Case Sensitive" value={args.caseSensitive ? "Yes" : "No"} />
                        {args.files?.length > 0 && <FilesList files={args.files} />}
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "grep_search":
                return (
                    <>
                        <ArgRow label="Pattern" value={args.pattern} icon={Search} />
                        <ArgRow label="Glob" value={args.glob} />
                        <ArgRow label="Case Insensitive" value={args.case_insensitive ? "Yes" : "No"} />
                        <ArgRow label="Context Lines" value={args.context_lines} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "glob_search":
                return (
                    <>
                        <ArgRow label="Pattern" value={args.pattern} icon={FolderSearch} />
                        <ArgRow label="Include Content" value={args.include_content ? "Yes" : "No"} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "explore_codebase":
                return (
                    <>
                        <ArgRow label="Query" value={args.query} />
                        <ArgRow label="Thoroughness" value={args.thoroughness} />
                        <ArgRow label="Scope" value={args.scope} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            case "search_api":
                return (
                    <>
                        <ArgRow label="Objective" value={args.objective} />
                        <ArgRow label="Reason" value={args.reason} />
                    </>
                );
            default:
                return (
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto ">
                        {JSON.stringify(args, null, 2)}
                    </pre>
                );
        }
    };

    return (
        <div className="space-y-1">
            {renderToolSpecificArgs()}
        </div>
    );
}
